import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

import { CampaignPostCard } from "@/components/CampaignPostCard";
import { CampaignTemplate } from "@/components/CampaignTemplate";
import {
  CAMPAIGN_POSTS,
  CAMPAIGN_WEEKS,
  CAMPAIGN_SCHEDULE,
} from "@/lib/campaign";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nf_campaign_token";

export default function Campaign() {
  const [token, setToken] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);

  const igPosts = CAMPAIGN_POSTS.filter((p) => p.id.startsWith("ig-"));
  const smPosts = CAMPAIGN_POSTS.filter((p) => p.id.startsWith("sm-"));

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setChecking(true);
    try {
      const res = await axios.post(`${API}/admin/verify`, {
        password: password.trim(),
      });
      if (res.data?.ok) {
        localStorage.setItem(STORAGE_KEY, password.trim());
        setToken(password.trim());
        setPassword("");
      } else {
        toast("That is not the key.");
      }
    } catch {
      toast("Something went wrong. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  if (!token) {
    return (
      <main
        data-testid="campaign-gate"
        className="bone-section min-h-[100svh] flex items-center justify-center px-6 md:px-12"
      >
        <div className="max-w-md w-full">
          <p className="hairline opacity-60 mb-10 text-center">
            Estate · Social Campaign
          </p>
          <h1 className="font-serif italic text-4xl md:text-5xl leading-tight text-center">
            The Campaign Room.
          </h1>
          <p className="font-serif italic text-lg md:text-xl opacity-70 mt-6 text-center">
            Enter the estate key to access the Chapter 2 campaign.
          </p>
          <form onSubmit={handleSignIn} className="mt-16">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="invisible-input text-center"
              placeholder="Key"
              data-testid="campaign-password-input"
              autoFocus
            />
            <div className="mt-10 flex items-center justify-between">
              <Link
                to="/"
                className="hairline opacity-50 hover:opacity-90 transition-opacity duration-700"
                data-testid="campaign-back-link"
              >
                ← Return
              </Link>
              <button
                type="submit"
                disabled={checking}
                className="link-quiet font-serif italic text-lg disabled:opacity-40"
                data-testid="campaign-submit-button"
              >
                {checking ? "…" : "Enter"}
              </button>
            </div>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="campaign-page" className="bone-section min-h-[100svh]">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-32 md:pb-48">
        <div className="flex items-center justify-between mb-16 md:mb-20">
          <p className="hairline opacity-60">Estate · Social Campaign</p>
          <div className="flex items-center gap-8">
            <Link
              to="/admin"
              className="hairline opacity-50 hover:opacity-90 transition-opacity duration-700"
              data-testid="campaign-admin-link"
            >
              Admin
            </Link>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem(STORAGE_KEY);
                setToken("");
              }}
              className="hairline opacity-50 hover:opacity-90 transition-opacity duration-700"
              data-testid="campaign-signout-button"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Intro */}
        <div className="max-w-4xl">
          <h1 className="font-wordmark text-6xl md:text-8xl leading-[0.95]">
            The Chapter 2 Campaign.
          </h1>
          <p className="font-serif italic text-xl md:text-2xl opacity-80 mt-8 leading-[1.4] max-w-[52ch]">
            Twenty posts across six weeks — from first introduction to final
            ticket call. Every graphic, caption, hashtag set and posting time,
            ready to publish. Built on the Chapter 2 visual identity: ink,
            bone, Six Caps and the paintings themselves.
          </p>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
            <a
              href={`${API}/press/campaign.pdf`}
              target="_blank"
              rel="noreferrer"
              className="hairline border border-[color:var(--ink)] px-6 py-3 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
              data-testid="campaign-pdf-download"
            >
              Download campaign PDF →
            </a>
            <a
              href="#templates"
              className="hairline opacity-70 hover:opacity-100 transition-opacity py-3"
              data-testid="campaign-templates-anchor"
            >
              Jump to template generator ↓
            </a>
          </div>
        </div>

        {/* Arc */}
        <section className="mt-24 md:mt-32" data-testid="campaign-calendar">
          <p className="hairline opacity-60 mb-3">I. The Arc</p>
          <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
            Six weeks. One story.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[color:var(--rule)] border border-[color:var(--rule)]">
            {CAMPAIGN_WEEKS.map((w) => {
              const weekPosts = CAMPAIGN_SCHEDULE.filter(
                (p) => p.week === w.week
              );
              return (
                <div
                  key={w.week}
                  className="bg-[color:var(--bone)] p-6 md:p-8"
                  data-testid={`campaign-week-${w.week}`}
                >
                  <p className="hairline opacity-50">
                    Week {w.week} · {w.dates}
                  </p>
                  <h3 className="font-serif italic text-2xl mt-3">{w.title}</h3>
                  <p className="font-serif text-base opacity-70 mt-2 leading-[1.5]">
                    {w.note}
                  </p>
                  <ul className="mt-5 space-y-1.5">
                    {weekPosts.map((p) => (
                      <li key={p.id} className="font-mono text-xs opacity-75">
                        {String(p.no).padStart(2, "0")} — {p.platform}:{" "}
                        {p.title}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* Instagram posts */}
        <section className="mt-24 md:mt-32" data-testid="campaign-ig-section">
          <p className="hairline opacity-60 mb-3">II. Instagram Feed</p>
          <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
            Ten feed posts.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-10">
            {igPosts.map((p) => (
              <CampaignPostCard key={p.id} post={p} />
            ))}
          </div>
        </section>

        {/* Platform posts */}
        <section className="mt-24 md:mt-32" data-testid="campaign-sm-section">
          <p className="hairline opacity-60 mb-3">
            III. TikTok · LinkedIn · Facebook · X · Stories
          </p>
          <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
            Ten platform posts.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-10">
            {smPosts.map((p) => (
              <CampaignPostCard key={p.id} post={p} />
            ))}
          </div>
        </section>

        {/* Template generator */}
        <section
          id="templates"
          className="mt-24 md:mt-32 scroll-mt-24"
          data-testid="campaign-template-section"
        >
          <p className="hairline opacity-60 mb-3">IV. Keep Posting</p>
          <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
            The template generator.
          </h2>
          <p className="font-serif italic text-lg md:text-xl opacity-70 mt-4 max-w-[52ch] leading-[1.5]">
            Type a headline, pick a painting, download a new on-brand graphic —
            so the campaign never has to stop at twenty posts.
          </p>
          <div className="mt-12">
            <CampaignTemplate />
          </div>
        </section>

        {/* Wall Label — Shoreditch Arts Club */}
        <section
          id="wall-label"
          className="mt-24 md:mt-32 scroll-mt-24"
          data-testid="wall-label-section"
        >
          <p className="hairline opacity-60 mb-3">V. On the wall</p>
          <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
            The Shoreditch wall label.
          </h2>
          <p className="font-serif italic text-lg md:text-xl opacity-70 mt-4 max-w-[54ch] leading-[1.5]">
            A print-ready A4 gallery label, framed alongside an original Nick
            Fraser painting inside Shoreditch Arts Club. Three design options —
            one artist, three registers.
          </p>

          <div className="mt-10">
            <a
              href={`${API}/press/wall-label.pdf?variant=all`}
              target="_blank"
              rel="noreferrer"
              className="hairline border border-[color:var(--ink)] px-6 py-3 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
              data-testid="wall-label-all-download"
            >
              Download all three options (PDF) →
            </a>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--rule)] border border-[color:var(--rule)]">
            {[
              {
                key: "minimal",
                num: "I",
                title: "Minimal Gallery Label",
                note:
                  "Museum white space. Serif wordmark, hairline rules, ink on bone. Reads like a permanent wall label in a public collection.",
              },
              {
                key: "heritage",
                num: "II",
                title: "Heritage Archive",
                note:
                  "Warmer paper stock feel, mounted portrait, PLATE captions and a soft archival band — a documentary tone from an East End estate.",
              },
              {
                key: "chapter2",
                num: "III",
                title: "Chapter 2 Signature",
                note:
                  "Deep ink and oxblood — the Chapter II wordmark treatment carried into the room. For members and collectors who know the exhibition.",
              },
            ].map((v) => (
              <div
                key={v.key}
                className="bg-[color:var(--bone)] p-6 md:p-8 flex flex-col"
                data-testid={`wall-label-card-${v.key}`}
              >
                <p className="hairline opacity-50">Option {v.num} · {v.title}</p>
                <div className="mt-4 aspect-[210/297] w-full bg-[color:var(--ink)] overflow-hidden">
                  <img
                    src={`${API}/press/wall-label.png?variant=${v.key}`}
                    alt={`${v.title} — A4 preview`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid={`wall-label-preview-${v.key}`}
                  />
                </div>
                <p className="font-serif text-base opacity-75 mt-5 leading-[1.55] flex-1">
                  {v.note}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={`${API}/press/wall-label.pdf?variant=${v.key}`}
                    target="_blank"
                    rel="noreferrer"
                    className="hairline border border-[color:var(--ink)] px-4 py-2 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500 text-xs"
                    data-testid={`wall-label-pdf-${v.key}`}
                  >
                    PDF (print-ready)
                  </a>
                  <a
                    href={`${API}/press/wall-label.png?variant=${v.key}`}
                    target="_blank"
                    rel="noreferrer"
                    download
                    className="hairline opacity-70 hover:opacity-100 transition-opacity px-4 py-2 text-xs"
                    data-testid={`wall-label-png-${v.key}`}
                  >
                    PNG (300dpi)
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="hairline opacity-50 mt-8 max-w-[70ch] leading-[1.6]">
            A4 portrait · 3mm bleed · crop marks · safe margins for framing ·
            QR links to chapter2.nickfraserartist.com. Sized for permanent
            display beside an original artwork at Shoreditch Arts Club.
          </p>
        </section>

        {/* Heritage Dossier — for museums, archives, funders */}
        <section
          id="heritage-dossier"
          className="mt-24 md:mt-32 scroll-mt-24"
          data-testid="heritage-dossier-section"
        >
          <p className="hairline opacity-60 mb-3">VI. For the record</p>
          <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
            The Heritage Dossier.
          </h2>
          <p className="font-serif italic text-lg md:text-xl opacity-70 mt-4 max-w-[58ch] leading-[1.5]">
            The foundational document for every future conversation — with
            museums, universities, researchers, galleries, heritage
            organisations and funders. Not a marketing deck; a professional
            heritage dossier in the register of a British Museum publication.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-6 max-w-[80ch]">
            <div>
              <p className="hairline opacity-50 mb-1">TITLE</p>
              <p className="font-serif text-base leading-[1.4]">
                The Nick Fraser Archive
                <br />
                <span className="italic opacity-80">Art, Labour and Life in East London</span>
              </p>
            </div>
            <div>
              <p className="hairline opacity-50 mb-1">PREPARED BY</p>
              <p className="font-serif text-base leading-[1.4]">
                Imaginative Reaction CIC
                <br />
                <span className="opacity-70">Curated by Marcus O&apos;Shaughnessy</span>
              </p>
            </div>
            <div>
              <p className="hairline opacity-50 mb-1">SPECIFICATION</p>
              <p className="font-serif text-base leading-[1.4]">
                A4 · 27 pages · 21 sections
                <br />
                <span className="opacity-70">Text-editable · bookmarked · clickable contents</span>
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={`${API}/press/heritage-dossier.pdf`}
              target="_blank"
              rel="noreferrer"
              className="hairline border border-[color:var(--ink)] px-6 py-3 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
              data-testid="heritage-dossier-download"
            >
              Download the Heritage Dossier (PDF) →
            </a>
            <a
              href={`${API}/press/heritage-dossier.pdf`}
              target="_blank"
              rel="noreferrer"
              className="hairline opacity-70 hover:opacity-100 transition-opacity px-6 py-3"
              data-testid="heritage-dossier-view"
            >
              Open in browser
            </a>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-5 max-w-[80ch] text-sm">
            {[
              "Executive Summary",
              "About Nick Fraser",
              "The Hidden Life",
              "Why the Archive Matters",
              "What Makes This Archive Unique",
              "East London",
              "Creativity Hidden in Plain Sight",
              "Artistic Practice",
              "The Works on Paper",
              "Documentary Evidence",
              "Why the Heritage is at Risk",
              "Professional Recognition",
              "Current Professional Conversations",
              "Why This Matters Nationally",
              "Public Benefit",
              "Heritage Project",
              "The Long-term Vision",
              "Letters of Recognition",
              "Press & Media",
              "Future Opportunities",
              "Contact",
            ].map((t, i) => (
              <div
                key={t}
                className="flex gap-3 items-baseline border-t border-[color:var(--rule)] pt-2"
              >
                <span className="hairline opacity-50 shrink-0 w-6">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif leading-[1.4]">{t}</span>
              </div>
            ))}
          </div>

          <p className="hairline opacity-50 mt-8 max-w-[74ch] leading-[1.6]">
            Includes verbatim letters of support from John Maizels (Publisher,
            Raw Vision magazine) and Kate Davey (PhD). Compiled from the
            estate&apos;s records, Strand Magazine, Raw Vision Issue 121 and
            Raw Vision Weekly #674. Corrections and additional letters welcomed
            at info@nickfraserartist.com.
          </p>
        </section>
      </div>
    </main>
  );
}
